import React from "react";
import styled from "styled-components";

const Container = styled.div`
  font-family: "Malgun Gothic", Arial, sans-serif;
  line-height: 1.6;
  margin: 0;
  padding: 0;
  color: #333;
  background-color: #f5f5f5;

  .container {
    max-width: 21cm;
    margin: 0 auto;
    padding: 2cm 20px;
    background-color: white;
    box-shadow: 0 0 10px rgba(0, 0, 0, 0.1);
    box-sizing: border-box;
  }

  .document-header {
    text-align: center;
    margin-bottom: 40px;
    padding: 30px;
    background: linear-gradient(135deg, #1e3a5f 0%, #4472a8 100%);
    color: white;
    border-radius: 15px;
    box-shadow: 0 4px 15px rgba(30, 58, 95, 0.3);
  }

  .document-title {
    font-size: 32px;
    font-weight: bold;
    margin: 0 0 20px 0;
    text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.3);
  }

  .document-subtitle {
    font-size: 18px;
    margin: 0 0 25px 0;
    opacity: 0.9;
    font-weight: 300;
  }

  .document-description {
    background-color: #f8f9fa;
    border: 2px solid #1e3a5f;
    border-radius: 10px;
    padding: 25px;
    margin: 30px 0;
    font-size: 16px;
    line-height: 1.8;
  }

  .description-title {
    color: #1e3a5f;
    font-size: 20px;
    font-weight: bold;
    margin: 0 0 15px 0;
    display: flex;
    align-items: center;
  }

  .description-title::before {
    content: "📋";
    margin-right: 10px;
    font-size: 24px;
  }

  .description-content {
    color: #333;
    margin-bottom: 20px;
  }

  .key-points {
    background-color: #e8f4f8;
    border-left: 5px solid #1e3a5f;
    padding: 15px 20px;
    margin: 15px 0;
  }

  .key-points h4 {
    color: #1e3a5f;
    margin: 0 0 10px 0;
    font-size: 16px;
    font-weight: bold;
  }

  .key-points ul {
    margin: 10px 0;
    padding-left: 20px;
  }

  .key-points li {
    margin: 8px 0;
    color: #333;
  }

  .header {
    text-align: center;
    margin-bottom: 50px;
    border-top: 2px solid #354380;
    border-bottom: 2px solid #354380;
    padding: 20px 0;
    background: linear-gradient(to right, #354380, white, white, #354380);
  }

  .header h1 {
    color: #354380;
    font-size: 28px;
    font-weight: bold;
    margin: 0;
    padding: 10px 0;
  }

  .section {
    margin: 30px 0;
  }

  .section h2 {
    color: #354380;
    border-bottom: 1px solid #354380;
    padding-bottom: 5px;
  }

  .section h3 {
    color: #354380;
    margin-top: 25px;
  }

  table {
    width: 100%;
    border-collapse: collapse;
    margin: 20px 0;
    word-break: keep-all;
    table-layout: fixed;
  }

  th,
  td {
    border: 1px solid #ddd;
    padding: 8px;
    text-align: left;
    word-wrap: break-word;
    word-break: break-word;
    overflow-wrap: break-word;
    hyphens: auto;
  }

  th {
    background-color: #e8f4f8;
  }

  .blue-header th {
    height: 40px;
    background-color: #214478;
    color: white;
    font-weight: bold;
    text-align: center;
    z-index: 10;
  }

  .gray-header td {
    background-color: #f5f5f5;
    font-weight: bold;
    position: sticky;
    top: 0px;
    z-index: 9;
  }

  .description {
    margin: 20px 0;
    padding: 15px;
    background-color: #f9f9f9;
    border-left: 4px solid #354380;
    font-style: italic;
  }

  ul {
    margin: 10px 0;
    padding-left: 20px;
  }

  li {
    margin: 5px 0;
  }
`;

const POLY: React.FC = () => {
  return (
    <Container>
      <div className="container">
        <div className="document-header">
          <div className="document-title">다제약물관리 서비스 약물 관리 가이드</div>
          <div className="document-subtitle">지역사회 약국 약사님들을 위한 실무 참고자료</div>
        </div>

        <div className="document-description">
          <div className="description-title">이 문서에 대해</div>
          <div className="description-content">
            본 가이드는 <strong>다제약물관리 교육자료</strong>에서 참고한 내용을 바탕으로, 지역사회 약국에서 근무하시는 약사님들이 환자의 약물 관리를 보다 체계적으로 수행할 수 있도록 도움을 드리기 위해 작성되었습니다.
          </div>
          
          <div className="key-points">
            <h4>🎯 이 가이드의 활용 방법</h4>
            <ul>
              <li><strong>다제약물관리사업 참여 약국:</strong> 서비스 수행 시 필수 점검 항목으로 활용</li>
              <li><strong>일반 지역약국:</strong> 환자 복약상담 및 약물 관리 시 참고자료로 활용</li>
              <li><strong>모든 약사:</strong> 환자 안전과 치료 효과 향상을 위한 실무 지침으로 활용</li>
            </ul>
          </div>

          <div className="key-points">
            <h4>📚 주요 내용</h4>
            <ul>
              <li><strong>복약 이행 점검 체크리스트:</strong> 복용 누락 시 위험도가 높은 약물들의 체계적 관리</li>
              <li><strong>고위험 약물 관리 목록:</strong> 특별한 주의가 필요한 약물들의 집중 관리</li>
              <li><strong>환자 교육자료 제공 대상:</strong> 약물별 맞춤형 교육이 필요한 경우의 지침</li>
            </ul>
          </div>

          <div className="key-points">
            <h4>💡 활용 팁</h4>
            <ul>
              <li>환자별 복용 약물을 이 리스트와 대조하여 우선순위 관리 대상을 파악하세요</li>
              <li>복약상담 시 해당 약물의 특별 주의사항을 환자에게 설명하세요</li>
              <li>정기적인 복약 이행도 점검을 통해 환자의 치료 효과를 극대화하세요</li>
            </ul>
          </div>
        </div>

        <div className="section">
          <h2>I. 복약 이행 점검 체크리스트</h2>

          <div className="description">
            이 약물들은 복용 누락 시 증상 악화, 치료 실패, 또는 약물 관련 부작용 가능성이 높아,
            약사가 복약이행 상태를 면밀히 점검해야 하는 대표적인 약제들입니다. 특히 일부 약물은 복용
            방법이 복잡하거나 다성분 병용이 이루어져 복약 오류 위험이 높고, 환자가 스스로 복용을
            중단하는 경우도 많기 때문에, 약사의 주기적인 평가와 복약상담을 통해 환자의 복약행동을
            확인하고, 필요한 교육이나 중재를 제공하는 것이 중요합니다.
          </div>

          <table>
            <tr className="blue-header">
              <th style={{ width: "7%" }}>번호</th>
              <th style={{ width: "25%" }}>분류</th>
              <th style={{ width: "68%" }}>약물명</th>
            </tr>

            <tr className="gray-header">
              <td colSpan={3}>증상 재발 및 악화 방지</td>
            </tr>
            <tr id="AC_1_01">
              <td style={{ whiteSpace: "nowrap" }}>1</td>
              <td>순환계</td>
              <td>이뇨제</td>
            </tr>
            <tr id="AC_1_02">
              <td style={{ whiteSpace: "nowrap" }}>2</td>
              <td>순환계</td>
              <td>ACEI</td>
            </tr>
            <tr id="AC_1_03">
              <td style={{ whiteSpace: "nowrap" }}>3</td>
              <td>순환계</td>
              <td>ARB</td>
            </tr>
            <tr id="AC_1_04">
              <td style={{ whiteSpace: "nowrap" }}>4</td>
              <td>내분비계</td>
              <td>스테로이드</td>
            </tr>
            <tr id="AC_1_05">
              <td style={{ whiteSpace: "nowrap" }}>5</td>
              <td>순환계</td>
              <td>심박수 조절 약물 (베타차단제, Diltiazem, Verapamil, Digoxin 등)</td>
            </tr>
            <tr id="AC_1_06">
              <td style={{ whiteSpace: "nowrap" }}>6</td>
              <td>신경계</td>
              <td>항전간제</td>
            </tr>
            <tr id="AC_1_07">
              <td style={{ whiteSpace: "nowrap" }}>7</td>
              <td>정신의학과</td>
              <td>기분 안정화제</td>
            </tr>
            <tr id="AC_1_08">
              <td style={{ whiteSpace: "nowrap" }}>8</td>
              <td>정신의학과</td>
              <td>항우울제</td>
            </tr>
            <tr id="AC_1_09">
              <td style={{ whiteSpace: "nowrap" }}>9</td>
              <td>순환계</td>
              <td>항협심증 약물</td>
            </tr>
            <tr id="AC_1_10">
              <td style={{ whiteSpace: "nowrap" }}>10</td>
              <td>신경계</td>
              <td>치매 치료제</td>
            </tr>
            <tr id="AC_1_11">
              <td style={{ whiteSpace: "nowrap" }}>11</td>
              <td>안과</td>
              <td>녹내장 치료 안약</td>
            </tr>
            <tr id="AC_1_12">
              <td style={{ whiteSpace: "nowrap" }}>12</td>
              <td>신경계</td>
              <td>Levodopa</td>
            </tr>
            <tr id="AC_1_13">
              <td style={{ whiteSpace: "nowrap" }}>13</td>
              <td>신경계</td>
              <td>Levodopa 복합제</td>
            </tr>
            <tr id="AC_1_14">
              <td style={{ whiteSpace: "nowrap" }}>14</td>
              <td>호흡기계</td>
              <td>천식 및 COPD 치료 흡입기</td>
            </tr>
            <tr id="AC_1_15">
              <td style={{ whiteSpace: "nowrap" }}>15</td>
              <td>피부과</td>
              <td>면역억제제 연고</td>
            </tr>
            <tr id="AC_1_16">
              <td style={{ whiteSpace: "nowrap" }}>16</td>
              <td>정신의학과</td>
              <td>항불안제</td>
            </tr>
            <tr id="AC_1_17">
              <td style={{ whiteSpace: "nowrap" }}>17</td>
              <td>감염내과</td>
              <td>항바이러스제 (HIV 치료제, B형/C형 간염 치료제)</td>
            </tr>
            <tr id="AC_1_18">
              <td style={{ whiteSpace: "nowrap" }}>18</td>
              <td>감염내과</td>
              <td>항생제</td>
            </tr>
            <tr id="AC_1_19">
              <td style={{ whiteSpace: "nowrap" }}>19</td>
              <td>감염내과</td>
              <td>항진균제</td>
            </tr>
            <tr id="AC_1_20">
              <td style={{ whiteSpace: "nowrap" }}>20</td>
              <td>감염내과</td>
              <td>항결핵제</td>
            </tr>

            <tr className="gray-header">
              <td colSpan={3}>질병의 급격한 악화 및 심각한 합병증 예방</td>
            </tr>
            <tr id="AC_2_01">
              <td style={{ whiteSpace: "nowrap" }}>21</td>
              <td>내분비계</td>
              <td>항당뇨병약제</td>
            </tr>
            <tr id="AC_2_02">
              <td style={{ whiteSpace: "nowrap" }}>22</td>
              <td>내분비계</td>
              <td>인슐린</td>
            </tr>
            <tr id="AC_2_03">
              <td style={{ whiteSpace: "nowrap" }}>23</td>
              <td>순환계</td>
              <td>혈압 조절 약물</td>
            </tr>
            <tr id="AC_2_04">
              <td style={{ whiteSpace: "nowrap" }}>24</td>
              <td>순환계</td>
              <td>Amiodarone</td>
            </tr>
            <tr id="AC_2_05">
              <td style={{ whiteSpace: "nowrap" }}>25</td>
              <td>순환계</td>
              <td>항부정맥제</td>
            </tr>
            <tr id="AC_2_06">
              <td style={{ whiteSpace: "nowrap" }}>26</td>
              <td>정신의학과</td>
              <td>항정신병약제</td>
            </tr>
            <tr id="AC_2_07">
              <td style={{ whiteSpace: "nowrap" }}>27</td>
              <td>류마티스내과</td>
              <td>DMARDs</td>
            </tr>
            <tr id="AC_2_08">
              <td style={{ whiteSpace: "nowrap" }}>28</td>
              <td>혈액종양내과</td>
              <td>세포독성/면역억제제</td>
            </tr>
            <tr id="AC_2_09">
              <td style={{ whiteSpace: "nowrap" }}>29</td>
              <td>류마티스내과</td>
              <td>methotrexate</td>
            </tr>
            <tr id="AC_2_10">
              <td style={{ whiteSpace: "nowrap" }}>30</td>
              <td>이식내과</td>
              <td>cyclosporine</td>
            </tr>
            <tr id="AC_2_11">
              <td style={{ whiteSpace: "nowrap" }}>31</td>
              <td>이식내과</td>
              <td>tacrolimus</td>
            </tr>
            <tr id="AC_2_12">
              <td style={{ whiteSpace: "nowrap" }}>32</td>
              <td>소화기내과</td>
              <td>간성혼수 예방 약물 (Lactulose, Rifaximin)</td>
            </tr>

            <tr className="gray-header">
              <td colSpan={3}>장기적 건강 및 예방 효과 유지</td>
            </tr>
            <tr id="AC_3_01">
              <td style={{ whiteSpace: "nowrap" }}>33</td>
              <td>순환계</td>
              <td>Statins</td>
            </tr>
            <tr id="AC_3_02">
              <td style={{ whiteSpace: "nowrap" }}>34</td>
              <td>내분비계</td>
              <td>골다공증약제 (Bisphosphonates, denosumab, teriparatide 등)</td>
            </tr>
            <tr id="AC_3_03">
              <td style={{ whiteSpace: "nowrap" }}>35</td>
              <td>내분비계</td>
              <td>여성 호르몬제</td>
            </tr>
            <tr id="AC_3_04">
              <td style={{ whiteSpace: "nowrap" }}>36</td>
              <td>비뇨기과</td>
              <td>Finasteride</td>
            </tr>

            <tr className="gray-header">
              <td colSpan={3}>금단 증상 예방</td>
            </tr>
            <tr id="AC_4_01">
              <td style={{ whiteSpace: "nowrap" }}>37</td>
              <td>내분비계</td>
              <td>스테로이드</td>
            </tr>
            <tr id="AC_4_02">
              <td style={{ whiteSpace: "nowrap" }}>38</td>
              <td>마취통증의학과</td>
              <td>마약성 진통제</td>
            </tr>
            <tr id="AC_4_03">
              <td style={{ whiteSpace: "nowrap" }}>39</td>
              <td>정신의학과</td>
              <td>항불안제</td>
            </tr>

            <tr className="gray-header">
              <td colSpan={3}>혈전 및 출혈 관리</td>
            </tr>
            <tr id="AC_5_01">
              <td style={{ whiteSpace: "nowrap" }}>40</td>
              <td>순환계</td>
              <td>항응고제</td>
            </tr>
            <tr id="AC_5_02">
              <td style={{ whiteSpace: "nowrap" }}>41</td>
              <td>순환계</td>
              <td>항혈소판제</td>
            </tr>
            <tr id="AC_5_03">
              <td style={{ whiteSpace: "nowrap" }}>42</td>
              <td>순환계</td>
              <td>Aspirin 포함 항혈소판제</td>
            </tr>

            <tr className="gray-header">
              <td colSpan={3}>혈액 및 호르몬 조절</td>
            </tr>
            <tr id="AC_6_01">
              <td style={{ whiteSpace: "nowrap" }}>43</td>
              <td>내분비계</td>
              <td>갑상선 호르몬제</td>
            </tr>
            <tr id="AC_6_02">
              <td style={{ whiteSpace: "nowrap" }}>44</td>
              <td>비뇨기과</td>
              <td>알파차단제</td>
            </tr>

            <tr className="gray-header">
              <td colSpan={3}>장기 복용으로 인한 부작용 관리</td>
            </tr>
            <tr id="AC_7_01">
              <td style={{ whiteSpace: "nowrap" }}>45</td>
              <td>내분비계</td>
              <td>스테로이드</td>
            </tr>
            <tr id="AC_7_02">
              <td style={{ whiteSpace: "nowrap" }}>46</td>
              <td>정신의학과</td>
              <td>항정신병약제</td>
            </tr>
          </table>
        </div>

        <div className="section">
          <h2>II. 고위험 약물 관리 목록</h2>

          <div className="description">
            약물 상호작용, 부작용, 복잡성, 용법 오류, 중복처방 가능성 등으로 인해 임상적으로 특별히
            주의가 필요한 약물들입니다.
          </div>

          <table>
            <tr className="blue-header">
              <th style={{ width: "7%" }}>번호</th>
              <th style={{ width: "93%" }}>고위험 약물명</th>
            </tr>
            <tr id="FMM_01">
              <td style={{ whiteSpace: "nowrap" }}>1</td>
              <td>마약성 진통제</td>
            </tr>
            <tr id="FMM_02">
              <td style={{ whiteSpace: "nowrap" }}>2</td>
              <td>항응고제</td>
            </tr>
            <tr id="FMM_03">
              <td style={{ whiteSpace: "nowrap" }}>3</td>
              <td>인슐린</td>
            </tr>
            <tr id="FMM_04">
              <td style={{ whiteSpace: "nowrap" }}>4</td>
              <td>삼환계 항우울제</td>
            </tr>
            <tr id="FMM_05">
              <td style={{ whiteSpace: "nowrap" }}>5</td>
              <td>중추신경계 억제 약물 (3성분 이상 병용 시)</td>
            </tr>
            <tr id="FMM_06">
              <td style={{ whiteSpace: "nowrap" }}>6</td>
              <td>항콜린성 약제 (2성분 이상 병용 시)</td>
            </tr>
            <tr id="FMM_07">
              <td style={{ whiteSpace: "nowrap" }}>7</td>
              <td>NSAIDs (2성분 이상 병용 시)</td>
            </tr>
            <tr id="FMM_08">
              <td style={{ whiteSpace: "nowrap" }}>8</td>
              <td>코르티코스테로이드 (부신피질호르몬제, 장기간 사용 시)</td>
            </tr>
            <tr id="FMM_09">
              <td style={{ whiteSpace: "nowrap" }}>9</td>
              <td>경구 혈당강하제 (3성분 이상 병용 시)</td>
            </tr>
            <tr id="FMM_10">
              <td style={{ whiteSpace: "nowrap" }}>10</td>
              <td>고혈압 약제 (3종 이상 병용 시)</td>
            </tr>
            <tr id="FMM_11">
              <td style={{ whiteSpace: "nowrap" }}>11</td>
              <td>소화기관용제 (3성분 이상 병용 시)</td>
            </tr>
            <tr id="FMM_12">
              <td style={{ whiteSpace: "nowrap" }}>12</td>
              <td>흡입기</td>
            </tr>
          </table>
        </div>

        <div className="section">
          <h2>III. 다제약물관리 교육자료 제공 대상</h2>

          <div className="description">
            이 목록은 약사가 다제약물관리 서비스를 수행할 때, 환자에게 제공할 수 있는 약물별
            교육자료 항목입니다.
          </div>

          <table>
            <tr className="blue-header">
              <th style={{ width: "7%" }}>번호</th>
              <th style={{ width: "93%" }}>교육자료 제공 대상</th>
            </tr>
            <tr id="edu_01">
              <td style={{ whiteSpace: "nowrap" }}>1</td>
              <td>Warfarin</td>
            </tr>
            <tr id="edu_02">
              <td style={{ whiteSpace: "nowrap" }}>2</td>
              <td>DOAC (Direct Oral Anticoagulants)</td>
            </tr>
            <tr id="edu_03">
              <td style={{ whiteSpace: "nowrap" }}>3</td>
              <td>
                고혈압약 3종류 이상 복용자 (단, 심부전, 부정맥, 비뇨기계 질환 등 다른 목적의 사용인
                경우에는 교육자료 제공 제외)
              </td>
            </tr>
            <tr id="edu_04">
              <td style={{ whiteSpace: "nowrap" }}>4</td>
              <td>경구 혈당강하제 3종 이상 복용자</td>
            </tr>
            <tr id="edu_05">
              <td style={{ whiteSpace: "nowrap" }}>5</td>
              <td>인슐린 사용 환자</td>
            </tr>
            <tr id="edu_06">
              <td style={{ whiteSpace: "nowrap" }}>6</td>
              <td>
                낙상주의 약물 복용자 (중추신경계 약물 3종 이상 또는 항콜린성 약제 2종 이상 또는
                삼환계 항우울제 복용 시)
              </td>
            </tr>
            <tr id="edu_07">
              <td style={{ whiteSpace: "nowrap" }}>7</td>
              <td>NSAIDs 복용자</td>
            </tr>
            <tr id="edu_08">
              <td style={{ whiteSpace: "nowrap" }}>8</td>
              <td>경구 코르티코이드 스테로이드 장기 복용자</td>
            </tr>
            <tr id="edu_09">
              <td style={{ whiteSpace: "nowrap" }}>9</td>
              <td>흡입기 사용 환자</td>
            </tr>
            <tr id="edu_10">
              <td style={{ whiteSpace: "nowrap" }}>10</td>
              <td>마약성 진통제 복용자</td>
            </tr>
            <tr id="edu_11">
              <td style={{ whiteSpace: "nowrap" }}>11</td>
              <td>소화기관용제 3성분 이상 병용자</td>
            </tr>
          </table>
        </div>
      </div>
    </Container>
  );
};

export default POLY;
